/**
 * SchemaContext - Global Schema Provider
 * 
 * Provides schema definitions and locale settings to the entire app
 * 
 * Security Note:
 * - Schema loading is only triggered when user is authenticated
 * - Default fallback schemas are used for unauthenticated state
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../lib/api-client';
import schemaService from '../../services/schema.service';

// Context
const SchemaContext = createContext(null);

// Default locale detection
const getDefaultLocale = () => {
    // Check localStorage first
    const saved = localStorage.getItem('app-locale');
    if (saved) return saved;
    
    // Check browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang.startsWith('zh-TW') || browserLang.startsWith('zh-Hant')) return 'zh-TW';
    if (browserLang.startsWith('zh')) return 'zh-CN';
    if (browserLang.startsWith('ja')) return 'ja';
    if (browserLang.startsWith('ko')) return 'ko';
    
    return 'en';
};

/**
 * SchemaProvider Component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated - Whether user is authenticated
 */
export function SchemaProvider({ children, isAuthenticated = false }) {
    const [locale, setLocaleState] = useState(getDefaultLocale());
    
    // Load global schema only when authenticated
    const { 
        data: globalSchema, 
        isLoading: schemaLoading,
        error: schemaError,
    } = useQuery({
        queryKey: ['schemas', 'global'],
        queryFn: async () => {
            try {
                const response = await apiClient.get('/schemas/global');
                return response.data;
            } catch {
                return schemaService.getDefaultGlobalSchema();
            }
        },
        staleTime: 60 * 60 * 1000, // 1 hour
        retry: 1,
        // Only fetch when authenticated
        enabled: isAuthenticated,
    });

    // Update schema service when global schema loads
    useEffect(() => {
        if (globalSchema) {
            schemaService.globalSchema = globalSchema;
        }
    }, [globalSchema]);

    // Update schema service when locale changes
    useEffect(() => {
        schemaService.setLocale(locale);
    }, [locale]);

    // Set locale
    const setLocale = useCallback((newLocale) => {
        setLocaleState(newLocale);
        schemaService.setLocale(newLocale);
        localStorage.setItem('app-locale', newLocale);
    }, []);

    // Translation function
    const t = useCallback((text) => {
        return schemaService.getLocalizedText(text, locale);
    }, [locale]);

    // Get field types
    const fieldTypes = useMemo(() => {
        return globalSchema?.fieldTypes?.enum || [];
    }, [globalSchema]);

    // Get step types
    const stepTypes = useMemo(() => {
        return globalSchema?.stepTypes?.enum || [];
    }, [globalSchema]);

    // Get role types
    const roleTypes = useMemo(() => {
        return globalSchema?.roleTypes?.enum || [];
    }, [globalSchema]);

    // Get action types
    const actionTypes = useMemo(() => {
        return globalSchema?.actionTypes?.enum || [];
    }, [globalSchema]);

    // Get currencies
    const currencies = useMemo(() => {
        return globalSchema?.currencies?.enum || [];
    }, [globalSchema]);

    // Get currency symbol
    const getCurrencySymbol = useCallback((code) => {
        return globalSchema?.currencies?.symbols?.[code] || code;
    }, [globalSchema]);

    // Context value
    const value = useMemo(() => ({
        // Schema
        globalSchema: globalSchema || schemaService.getDefaultGlobalSchema(),
        schemaLoading,
        schemaError,
        
        // Locale
        locale,
        setLocale,
        t,
        getLocalizedText: t,
        
        // Schema helpers
        fieldTypes,
        stepTypes,
        roleTypes,
        actionTypes,
        currencies,
        getCurrencySymbol,
        
        // Schema service reference
        schemaService,
    }), [
        globalSchema,
        schemaLoading,
        schemaError,
        locale,
        setLocale,
        t,
        fieldTypes,
        stepTypes,
        roleTypes,
        actionTypes,
        currencies,
        getCurrencySymbol,
    ]);

    return (
        <SchemaContext.Provider value={value}>
            {children}
        </SchemaContext.Provider>
    );
}

/**
 * useSchemaContext Hook
 */
export function useSchemaContext() {
    const context = useContext(SchemaContext);
    if (!context) {
        throw new Error('useSchemaContext must be used within a SchemaProvider');
    }
    return context;
}

export default SchemaContext;
